import { useSelector } from 'react-redux';
import { createAction, createReducer } from 'redux-act';

import { useIsMobile } from '@sravni/react-utils';
import type { IBranding, IConfig } from '@sravni/types/lib/branding';

import type StatusData from '@src/@types/StatusData';

import type { IGlobalState } from '..';
import DataStatus from '../../enums/DataStatus';

// @ts-ignore
import mainLogo from './assets/mainLogo.png';
// @ts-ignore
import partnerName from './assets/partnerName.png';

type IState = StatusData<IBranding | null> & {
  scrolledDown: boolean;
};

const initialState: IState = {
  data: null,
  status: DataStatus.Default,
  scrolledDown: false,
};

export const setBranding = createAction<Partial<IState>>('branding/set');
export const setScrolledDown = createAction<IState['scrolledDown']>('branding/scrolledDown/set/');

export const brandingReducer = createReducer<IState>({}, initialState)
  .on(setBranding, (state, payload) => ({
    ...state,
    ...payload,
  }))
  .on(setScrolledDown, (state, payload) => ({
    ...state,
    scrolledDown: payload,
  }));

const getBranding = (state: IGlobalState) => state.branding.data;
const getIsScrolledDown = (state: IGlobalState) => state.branding.scrolledDown;
const getDesktopBranding = (state: IGlobalState) => {
  const data = getBranding(state);

  return data?.desktop;
};

const getMobileBranding = (state: IGlobalState) => {
  const data = getBranding(state);

  return data?.mobile;
};

const getPixelsBranding = (state: IGlobalState) => state?.branding?.data?.pixels;

export function useBranding(): IState['data'] {
  return useSelector<IGlobalState, IState['data']>(getBranding);
}
export function useDesktopBranding(): IConfig | undefined {
  return useSelector<IGlobalState, IConfig | undefined>(getDesktopBranding);
}
export function useMobileBranding(): IConfig | undefined {
  return useSelector<IGlobalState, IConfig | undefined>(getMobileBranding);
}
export function usePixelsBranding(): IBranding['pixels'] | undefined {
  return useSelector<IGlobalState, IBranding['pixels'] | undefined>(getPixelsBranding);
}
export function useDeviceBranding(): IConfig | undefined {
  const isMobile = useIsMobile();
  const mobileBranding = useMobileBranding();
  const desktopBranding = useDesktopBranding();
  return isMobile ? mobileBranding : desktopBranding;
}

export function useIsBrandingScrolledDown(): IState['scrolledDown'] {
  return useSelector<IGlobalState, IState['scrolledDown']>(getIsScrolledDown);
}

const IMAGE_SIZES = {
  mobile: { height: 168 },
  desktop: { height: 213 },
} as const;

const IMAGE_SIZES_LOGO = {
  mobile: { width: 192, height: 84 },
  desktop: { width: 192, height: 84 },
} as const;

const DEFAULT_PIXELS = {
  click:
    'https://goto.startracking.ru/api/v1/redirect?offer_id=101096&aff_id=2&erid=2W5zFHQ83UM&random=ovA8zkaNps_hnn7WVijxu',
  display:
    'https://goto.startracking.ru/api/v1/impression?offer_id=101096&aff_id=2&erid=2W5zFHQ83UM&random=ovA8zkaNps_hnn7WVijxu',
  tracking:
    'https://amc.yandex.ru/show?cmn_id=103007&plt_id=446002&crv_id=976847&evt_t=render&ad_type=banner&rnd=ovA8zkaNps_hnn7WVijxu',
} as const;

const DEFAULT_SPONSOR_TEXTS = {
  popoverTitle: 'Реклама ООО МФК «Мани Мен» ИНН: 7704784072',
  title:
    'Реклама ООО МФК «Мани Мен» ИНН: 7704784072. Оценивайте свои финансовые возможности и риски. Изучите все условия займа на сайте moneyman.ru/lp/mkk/',
  psk: 'Полная стоимость кредита (займа) 281%–292% годовых. Ставка 0% действует в беспроцентный период при первом обращении.',
  buttonText: 'Подобрать займ',
} as const;

export function useBrandingContent() {
  const isMobile = useIsMobile();
  const branding = useBranding();

  const platform = isMobile ? 'mobile' : 'desktop';

  const target = branding?.[platform];

  const sponsor = target?.calculatorSponsor as Record<string, any>;
  const calculator = target?.creditCalculator;

  return {
    analytics: branding?.pixels ?? DEFAULT_PIXELS,
    sponsor: {
      popover: {
        title: sponsor?.additionalTextTwo || DEFAULT_SPONSOR_TEXTS.popoverTitle,
      },
      title: sponsor?.additionalTextOne || DEFAULT_SPONSOR_TEXTS.title,
      description: sponsor?.text ?? '',
      psk: sponsor?.psk || DEFAULT_SPONSOR_TEXTS.psk,

      buttonText: sponsor?.text || DEFAULT_SPONSOR_TEXTS.buttonText,
      buttonStyles: {
        backgroundColor: (calculator as any)?.buttonBackground ?? '#74A509',
        color: (calculator as any)?.buttonColor ?? '#FFFFFF',
      },
    },
    logo: {
      ...IMAGE_SIZES_LOGO[platform],
      src: sponsor?.logo?.webp ?? sponsor?.logo?.original ?? partnerName,
    },
    image: {
      ...IMAGE_SIZES[platform],
      src: calculator?.backgroundImage?.webp ?? calculator?.backgroundImage?.original ?? mainLogo,
    },
  };
}
